/**
 * 数据大小与单位转换工具
 */

export interface SizeFormatOptions {
  base?: 'b' | 'k' | 'm' | 'g'
  decimals?: number
  target?: 'b' | 'k' | 'm' | 'g'
}

/**
 * 格式化人类可读的大小
 * @param size 大小数值
 * @param options 配置选项
 * @returns 格式化后的字符串或数值
 */
export function formatHumanSize(
  size: number,
  options?: SizeFormatOptions
): string | number {
  const { base = 'b', decimals = 1, target } = options || {}

  if (size === 0) return target ? 0 : '0 B'

  const units = {
    b: { name: 'B', multiplier: 1 },
    k: { name: 'KiB', multiplier: 1024 },
    m: { name: 'MiB', multiplier: 1024 * 1024 },
    g: { name: 'GiB', multiplier: 1024 * 1024 * 1024 },
    t: { name: 'TiB', multiplier: 1024 * 1024 * 1024 * 1024 },
    p: { name: 'PiB', multiplier: 1024 * 1024 * 1024 * 1024 * 1024 }
  }

  const dm = decimals < 0 ? 0 : decimals

  // 将输入转换为字节
  const bytes = size * units[base].multiplier

  // 如果指定了目标单位，直接转换
  if (target) {
    const result = bytes / units[target].multiplier
    return Math.round(result * Math.pow(10, dm)) / Math.pow(10, dm)
  }

  // 自动选择最合适的单位
  const unitKeys = Object.keys(units) as Array<keyof typeof units>
  let bestUnit: keyof typeof units = 'b'

  for (let i = unitKeys.length - 1; i >= 0; i--) {
    const unit = unitKeys[i]
    if (bytes >= units[unit].multiplier) {
      bestUnit = unit
      break
    }
  }

  const result = bytes / units[bestUnit].multiplier
  const formatted = parseFloat(result.toFixed(dm))

  return `${formatted}${units[bestUnit].name}`
}

/**
 * 数据格式化器集合
 */
export const formatters = {
  percentage: (value: number) => `${value.toFixed(1)}%`,
  memory: (value: number) => {
    if (value >= 1024) {
      return `${(value / 1024).toFixed(1)} GB`
    }
    return `${value.toFixed(1)} MB`
  },
  bytes: (value: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let unitIndex = 0
    let size = value

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`
  }
}
